import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { apiKeysAPI } from '../services/api';

const ApiDocs = () => {
  const [activeTab, setActiveTab] = useState('docs');
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [expandedEndpoint, setExpandedEndpoint] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '', descripcion: '', permisos: ['catalogo:leer', 'clientes:leer'],
    rate_limit: 100, ip_whitelist: ''
  });

  const cargarApiKeys = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiKeysAPI.getAll();
      setApiKeys(data.data || []);
    } catch {
      toast.error('Error al cargar API Keys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'keys') cargarApiKeys();
  }, [activeTab, cargarApiKeys]);

  const handleCreate = async () => {
    if (!formData.nombre.trim()) {
      toast.warning('El nombre es requerido');
      return;
    }
    try {
      const payload = {
        ...formData,
        ip_whitelist: formData.ip_whitelist ? formData.ip_whitelist.split(',').map(ip => ip.trim()).filter(Boolean) : null
      };
      await apiKeysAPI.create(payload);
      toast.success('API Key creada exitosamente');
      setShowCreateModal(false);
      resetForm();
      cargarApiKeys();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al crear API Key');
    }
  };

  const handleUpdate = async () => {
    try {
      const payload = {
        ...formData,
        ip_whitelist: formData.ip_whitelist ? formData.ip_whitelist.split(',').map(ip => ip.trim()).filter(Boolean) : null
      };
      await apiKeysAPI.update(editingKey.id, payload);
      toast.success('API Key actualizada');
      setEditingKey(null);
      resetForm();
      cargarApiKeys();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al actualizar');
    }
  };

  const handleDelete = async (id, nombre) => {
    if (!window.confirm(`¿Eliminar la API Key "${nombre}"? Esta acción no se puede deshacer.`)) return;
    try {
      await apiKeysAPI.delete(id);
      toast.success('API Key eliminada');
      cargarApiKeys();
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const handleToggleActive = async (key) => {
    try {
      await apiKeysAPI.update(key.id, { activa: !key.activa });
      toast.success(key.activa ? 'API Key desactivada' : 'API Key activada');
      cargarApiKeys();
    } catch {
      toast.error('Error al cambiar estado');
    }
  };

  const handleRegenerar = async (id, nombre) => {
    if (!window.confirm(`¿Regenerar la API Key "${nombre}"? La key anterior dejará de funcionar.`)) return;
    try {
      await apiKeysAPI.regenerar(id);
      toast.success('API Key regenerada');
      cargarApiKeys();
    } catch {
      toast.error('Error al regenerar');
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: '', descripcion: '', permisos: ['catalogo:leer', 'clientes:leer'],
      rate_limit: 100, ip_whitelist: ''
    });
  };

  const openEdit = (key) => {
    setFormData({
      nombre: key.nombre,
      descripcion: key.descripcion || '',
      permisos: key.permisos || [],
      rate_limit: key.rate_limit || 100,
      ip_whitelist: key.ip_whitelist ? key.ip_whitelist.join(', ') : ''
    });
    setEditingKey(key);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.info('Copiado al portapapeles');
  };

  const togglePermiso = (permiso) => {
    setFormData(prev => ({
      ...prev,
      permisos: prev.permisos.includes(permiso)
        ? prev.permisos.filter(p => p !== permiso)
        : [...prev.permisos, permiso]
    }));
  };

  const BASE_URL = window.location.origin + '/api/v1/public';

  const permisosDisponibles = [
    { key: 'catalogo:leer', label: 'Catálogo (Lectura)', desc: 'Productos, categorías, marcas' },
    { key: 'clientes:leer', label: 'Clientes (Lectura)', desc: 'Consultar clientes' },
    { key: 'clientes:escribir', label: 'Clientes (Escritura)', desc: 'Crear clientes' }
  ];

  const endpoints = [
    {
      section: 'Catálogo de Productos',
      items: [
        {
          method: 'GET', path: '/productos', permiso: 'catalogo:leer',
          desc: 'Listar productos del catálogo con filtros y paginación',
          params: [
            { name: 'buscar', type: 'string', desc: 'Buscar por nombre, código o descripción' },
            { name: 'categoria', type: 'integer', desc: 'Filtrar por ID de categoría' },
            { name: 'marca', type: 'integer', desc: 'Filtrar por ID de marca' },
            { name: 'tipo_producto', type: 'string', desc: 'Filtrar por tipo: armazon, lente_oftalmico, lente_contacto, lente_sol, accesorio, solucion, servicio' },
            { name: 'genero', type: 'string', desc: 'Filtrar por género: masculino, femenino, unisex, infantil' },
            { name: 'page', type: 'integer', desc: 'Página (default: 1)' },
            { name: 'limit', type: 'integer', desc: 'Items por página (default: 20, max: 100)' }
          ],
          note: 'Respuesta: Array directo (1 item por producto en n8n). Paginación en headers: X-Total-Count, X-Page, X-Limit, X-Total-Pages',
          response: `[
  {
    "id": 1,
    "codigo": "ARM-001",
    "nombre": "Armazón Ray-Ban RB5154",
    "descripcion": "Armazón clásico clubmaster",
    "categoria": "Armazones",
    "marca": "Ray-Ban",
    "tipo_producto": "armazon",
    "genero": "unisex",
    "precio_venta": 85.00,
    "iva_porcentaje": 15,
    "precio_con_iva": 97.75,
    "stock_actual": 12,
    "estado_stock": "normal",
    "modelo": "RB5154",
    "color": "Negro/Dorado",
    "material": "Acetato/Metal",
    "imagenes": [
      { "url": "https://sistema.oftalmetryc.com/cdn/uuid.jpg", "es_principal": true, "orden": 0 },
      { "url": "https://sistema.oftalmetryc.com/cdn/uuid2.jpg", "es_principal": false, "orden": 1 }
    ],
    "videos": [
      { "url": "https://sistema.oftalmetryc.com/cdn/uuid3.mp4", "duration_seconds": 15.5, "orden": 0 }
    ]
  }
]

// Headers de paginación:
// X-Total-Count: 150
// X-Page: 1
// X-Limit: 20
// X-Total-Pages: 8`
        },
        {
          method: 'GET', path: '/productos/:id', permiso: 'catalogo:leer',
          desc: 'Obtener detalle completo de un producto',
          params: [{ name: 'id', type: 'integer', desc: 'ID del producto (en URL)' }],
          note: 'Respuesta: Objeto directo del producto (sin wrapper success/data)',
          response: `{
  "id": 1,
  "codigo": "ARM-001",
  "nombre": "Armazón Ray-Ban RB5154",
  "descripcion": "Armazón clásico clubmaster",
  "categoria": "Armazones",
  "marca": "Ray-Ban",
  "proveedor": "Luxottica Ecuador",
  "tipo_producto": "armazon",
  "genero": "unisex",
  "precio_venta": 85.00,
  "iva_porcentaje": 15,
  "precio_con_iva": 97.75,
  "stock_actual": 12,
  "stock_minimo": 3,
  "estado_stock": "normal",
  "modelo": "RB5154",
  "color": "Negro/Dorado",
  "material": "Acetato/Metal",
  "medida_puente": 21,
  "medida_varilla": 145,
  "medida_horizontal": 51,
  "medida_vertical": 42,
  "imagenes": [
    { "url": "https://sistema.oftalmetryc.com/cdn/uuid.jpg", "es_principal": true, "orden": 0, "width": 1200, "height": 900 }
  ],
  "videos": [
    { "url": "https://sistema.oftalmetryc.com/cdn/uuid.mp4", "duration_seconds": 30.5, "orden": 0, "size_bytes": 5242880 }
  ]
}`
        },
        {
          method: 'GET', path: '/categorias', permiso: 'catalogo:leer',
          desc: 'Listar todas las categorías activas',
          params: [],
          response: `{
  "success": true,
  "data": [
    { "id": 1, "nombre": "Armazones", "descripcion": "Monturas para lentes", "iva_porcentaje": 15 },
    { "id": 2, "nombre": "Lentes Oftálmicos", "descripcion": "Lentes graduados", "iva_porcentaje": 0 }
  ]
}`
        },
        {
          method: 'GET', path: '/marcas', permiso: 'catalogo:leer',
          desc: 'Listar todas las marcas activas',
          params: [],
          response: `{
  "success": true,
  "data": [
    { "id": 1, "nombre": "Ray-Ban", "descripcion": "Marca líder mundial" },
    { "id": 2, "nombre": "Oakley", "descripcion": "Deportivos y casuales" }
  ]
}`
        }
      ]
    },
    {
      section: 'Clientes / Pacientes',
      items: [
        {
          method: 'GET', path: '/clientes', permiso: 'clientes:leer',
          desc: 'Listar clientes con búsqueda y paginación',
          params: [
            { name: 'buscar', type: 'string', desc: 'Buscar por cédula, nombre, teléfono o email' },
            { name: 'tipo_cliente', type: 'string', desc: 'Filtrar: regular, frecuente, vip, convenio' },
            { name: 'page', type: 'integer', desc: 'Página (default: 1)' },
            { name: 'limit', type: 'integer', desc: 'Items por página (default: 20, max: 100)' }
          ],
          response: `{
  "success": true,
  "data": [
    {
      "id": 1,
      "codigo": "CLI-001",
      "tipo_persona": "natural",
      "tipo_identificacion": "cedula",
      "identificacion": "0912345678",
      "nombres": "Juan Carlos",
      "apellidos": "Pérez López",
      "telefono": "042123456",
      "celular": "0991234567",
      "email": "juan@email.com",
      "direccion": "Av. Principal 123",
      "ciudad": "Guayaquil",
      "tipo_cliente": "regular",
      "usa_lentes": true,
      "ultimo_control_visual": "2025-06-15"
    }
  ],
  "pagination": { "total": 85, "page": 1, "limit": 20, "totalPages": 5 }
}`
        },
        {
          method: 'GET', path: '/clientes/:id', permiso: 'clientes:leer',
          desc: 'Obtener detalle completo de un cliente',
          params: [{ name: 'id', type: 'integer', desc: 'ID del cliente (en URL)' }],
          response: `{
  "success": true,
  "data": {
    "id": 1,
    "codigo": "CLI-001",
    "tipo_persona": "natural",
    "identificacion": "0912345678",
    "nombres": "Juan Carlos",
    "apellidos": "Pérez López",
    "fecha_nacimiento": "1985-03-15",
    "genero": "masculino",
    "telefono": "042123456",
    "celular": "0991234567",
    "email": "juan@email.com",
    "whatsapp": "0991234567",
    "direccion": "Av. Principal 123",
    "ciudad": "Guayaquil",
    "provincia": "Guayas",
    "ocupacion": "Ingeniero",
    "usa_lentes": true,
    "ultimo_control_visual": "2025-06-15",
    "tipo_cliente": "regular"
  }
}`
        },
        {
          method: 'GET', path: '/clientes/buscar-ci?ci=XXXXXXXXXX', permiso: 'clientes:leer',
          desc: 'Buscar cliente por número de cédula/identificación',
          params: [{ name: 'ci', type: 'string', desc: 'Número de cédula o RUC (query param requerido)' }],
          response: `{
  "success": true,
  "data": {
    "id": 1,
    "codigo": "CLI-001",
    "identificacion": "0912345678",
    "nombres": "Juan Carlos",
    "apellidos": "Pérez López",
    "celular": "0991234567",
    "email": "juan@email.com",
    "tipo_cliente": "regular"
  }
}`
        },
        {
          method: 'POST', path: '/clientes', permiso: 'clientes:escribir',
          desc: 'Crear un nuevo cliente/paciente (útil para registro desde bot)',
          params: [
            { name: 'identificacion', type: 'string', desc: 'Cédula o RUC (requerido)' },
            { name: 'nombres', type: 'string', desc: 'Nombres del cliente (requerido)' },
            { name: 'apellidos', type: 'string', desc: 'Apellidos del cliente' },
            { name: 'tipo_persona', type: 'string', desc: 'natural o juridica (default: natural)' },
            { name: 'tipo_identificacion', type: 'string', desc: 'cedula, ruc, pasaporte (default: cedula)' },
            { name: 'telefono', type: 'string', desc: 'Teléfono fijo' },
            { name: 'celular', type: 'string', desc: 'Número de celular' },
            { name: 'email', type: 'string', desc: 'Correo electrónico' },
            { name: 'whatsapp', type: 'string', desc: 'Número de WhatsApp' },
            { name: 'direccion', type: 'string', desc: 'Dirección' },
            { name: 'ciudad', type: 'string', desc: 'Ciudad' },
            { name: 'provincia', type: 'string', desc: 'Provincia' },
            { name: 'tipo_cliente', type: 'string', desc: 'regular, frecuente, vip, convenio (default: regular)' },
            { name: 'notas', type: 'string', desc: 'Notas adicionales' }
          ],
          response: `{
  "success": true,
  "data": {
    "id": 86,
    "codigo": "CLI-086",
    "identificacion": "0987654321",
    "nombres": "María",
    "apellidos": "González"
  }
}

// Error 409 si ya existe:
{
  "success": false,
  "error": "Ya existe un cliente con esa identificación",
  "data": { "id": 15 }
}`
        }
      ]
    }
  ];

  const getCurlCommand = (ep) => {
    if (ep.method === 'POST') {
      return `curl -X POST "${BASE_URL}${ep.path}" \\\n  -H "X-API-Key: $API_KEY" \\\n  -H "Content-Type: application/json" \\\n  -d '${ep.curlBody || '{"identificacion":"0912345678","nombres":"Juan","apellidos":"Pérez"}'}'`;
    }
    return `curl -X GET "${BASE_URL}${ep.path.replace(':id', '1')}" \\\n  -H "X-API-Key: $API_KEY"`;
  };

  const getMethodColor = (method) => {
    const colors = { GET: '#28a745', POST: '#007bff', PUT: '#ffc107', DELETE: '#dc3545' };
    return colors[method] || '#6c757d';
  };

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h4 className="mb-1">
                <i className="bi bi-code-slash me-2 text-primary"></i>
                API Pública - Documentación
              </h4>
              <p className="text-muted mb-0">
                API REST para integración con n8n, bots de WhatsApp y plataformas de automatización
              </p>
            </div>
            <span className="badge bg-success fs-6">v1.1</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button className={`nav-link ${activeTab === 'docs' ? 'active' : ''}`} onClick={() => setActiveTab('docs')}>
            <i className="bi bi-book me-1"></i> Documentación
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${activeTab === 'keys' ? 'active' : ''}`} onClick={() => setActiveTab('keys')}>
            <i className="bi bi-key me-1"></i> API Keys
          </button>
        </li>
      </ul>

      {/* TAB: Documentación */}
      {activeTab === 'docs' && (
        <div>
          {/* Autenticación */}
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-dark text-white">
              <h5 className="mb-0"><i className="bi bi-shield-lock me-2"></i>Autenticación</h5>
            </div>
            <div className="card-body">
              <p>Todas las peticiones requieren un header <code>X-API-Key</code> con una API Key válida.</p>
              <div className="position-relative">
                <div className="bg-dark text-light p-3 rounded font-monospace small">
                  <span className="text-info">curl</span> -X GET "{BASE_URL}/productos" \<br />
                  &nbsp;&nbsp;-H "<span className="text-warning">X-API-Key: oft_tu_api_key_aqui</span>"
                </div>
                <button
                  className="btn btn-sm btn-outline-light position-absolute"
                  style={{ top: '8px', right: '8px' }}
                  onClick={() => copyToClipboard(`curl -X GET "${BASE_URL}/productos" \\\n  -H "X-API-Key: oft_tu_api_key_aqui"`)}
                  title="Copiar cURL al portapapeles"
                >
                  <i className="bi bi-clipboard"></i>
                </button>
              </div>
              <div className="mt-3">
                <h6>Códigos de Error de Autenticación</h6>
                <table className="table table-sm table-bordered">
                  <thead className="table-light">
                    <tr><th>Código</th><th>Descripción</th></tr>
                  </thead>
                  <tbody>
                    <tr><td><code>401</code></td><td>API Key no proporcionada o inválida</td></tr>
                    <tr><td><code>403</code></td><td>API Key no tiene permiso para este recurso, o IP no autorizada</td></tr>
                    <tr><td><code>429</code></td><td>Límite de peticiones excedido (rate limit)</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Base URL */}
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body">
              <h6 className="text-muted mb-2">Base URL</h6>
              <div className="d-flex align-items-center gap-2">
                <code className="bg-light px-3 py-2 rounded flex-grow-1">{BASE_URL}</code>
                <button className="btn btn-sm btn-outline-primary" onClick={() => copyToClipboard(BASE_URL)}>
                  <i className="bi bi-clipboard"></i>
                </button>
              </div>
            </div>
          </div>

          {/* Endpoints */}
          {endpoints.map((section, sIdx) => (
            <div key={sIdx} className="card border-0 shadow-sm mb-4">
              <div className="card-header bg-white">
                <h5 className="mb-0">{section.section}</h5>
              </div>
              <div className="card-body p-0">
                {section.items.map((ep, eIdx) => {
                  const epKey = `${sIdx}-${eIdx}`;
                  const isExpanded = expandedEndpoint === epKey;
                  return (
                    <div key={eIdx} className="border-bottom">
                      <div
                        className="d-flex align-items-center p-3 cursor-pointer"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setExpandedEndpoint(isExpanded ? null : epKey)}
                      >
                        <span
                          className="badge me-3 px-2 py-1"
                          style={{ backgroundColor: getMethodColor(ep.method), minWidth: '55px', fontSize: '0.75rem' }}
                        >
                          {ep.method}
                        </span>
                        <code className="me-3 text-dark">{ep.path}</code>
                        <span className="text-muted small flex-grow-1">{ep.desc}</span>
                        <span className="badge bg-light text-dark border me-2">{ep.permiso}</span>
                        <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'} text-muted`}></i>
                      </div>
                      {isExpanded && (
                        <div className="px-4 pb-4 bg-light">
                          {ep.params && ep.params.length > 0 && (
                            <div className="mb-3">
                              <h6 className="text-muted mb-2">
                                {ep.method === 'POST' ? 'Body (JSON)' : 'Parámetros'}
                              </h6>
                              <table className="table table-sm table-bordered bg-white mb-0">
                                <thead className="table-light">
                                  <tr><th>Nombre</th><th>Tipo</th><th>Descripción</th></tr>
                                </thead>
                                <tbody>
                                  {ep.params.map((p, pIdx) => (
                                    <tr key={pIdx}>
                                      <td><code>{p.name}</code></td>
                                      <td><span className="badge bg-secondary">{p.type}</span></td>
                                      <td className="small">{p.desc}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                          {ep.note && (
                            <div className="alert alert-info py-2 px-3 small mb-3">
                              <i className="bi bi-info-circle me-1"></i>{ep.note}
                            </div>
                          )}
                          <div>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <h6 className="text-muted mb-0">Respuesta de ejemplo</h6>
                              <button
                                className="btn btn-sm btn-outline-light border-secondary text-secondary"
                                onClick={() => copyToClipboard(ep.response)}
                                title="Copiar respuesta"
                              >
                                <i className="bi bi-clipboard me-1"></i>Copiar
                              </button>
                            </div>
                            <pre className="bg-dark text-light p-3 rounded small mb-0" style={{ maxHeight: '400px', overflow: 'auto' }}>
                              {ep.response}
                            </pre>
                          </div>
                          <div className="mt-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <h6 className="text-muted mb-0">Ejemplo con cURL</h6>
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => copyToClipboard(getCurlCommand(ep))}
                                title="Copiar cURL al portapapeles"
                              >
                                <i className="bi bi-clipboard me-1"></i>Copiar cURL
                              </button>
                            </div>
                            <div className="bg-dark text-light p-3 rounded font-monospace small position-relative">
                              {ep.method === 'POST' ? (
                                <>
                                  <span className="text-info">curl</span> -X POST "{BASE_URL}{ep.path}" \<br />
                                  &nbsp;&nbsp;-H "<span className="text-warning">X-API-Key: $API_KEY</span>" \<br />
                                  &nbsp;&nbsp;-H "Content-Type: application/json" \<br />
                                  &nbsp;&nbsp;-d '{ep.curlBody || `{"identificacion":"0912345678","nombres":"Juan","apellidos":"Pérez"}`}'
                                </>
                              ) : (
                                <>
                                  <span className="text-info">curl</span> -X GET "{BASE_URL}{ep.path.replace(':id', '1')}" \<br />
                                  &nbsp;&nbsp;-H "<span className="text-warning">X-API-Key: $API_KEY</span>"
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Notas de integración */}
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-info bg-opacity-10">
              <h5 className="mb-0 text-info"><i className="bi bi-lightbulb me-2"></i>Notas de Integración</h5>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <h6><i className="bi bi-robot me-1"></i>Bot de WhatsApp (n8n)</h6>
                  <ul className="small text-muted mb-0">
                    <li>Usa <code>GET /clientes/buscar-ci?ci=</code> para identificar al cliente</li>
                    <li>Si no existe, usa <code>POST /clientes</code> para registrarlo</li>
                    <li>Consulta el catálogo con <code>GET /productos</code> filtrando por tipo</li>
                    <li><strong>Productos</strong> devuelve array directo (1 item por producto en n8n)</li>
                    <li>Clientes y otros endpoints usan <code>{`{ success, data }`}</code></li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h6><i className="bi bi-arrow-repeat me-1"></i>Paginación</h6>
                  <ul className="small text-muted mb-0">
                    <li>Máximo 100 items por página, default: 20</li>
                    <li><strong>Productos:</strong> Paginación en headers HTTP</li>
                    <li className="ms-3"><code>X-Total-Count</code>, <code>X-Page</code>, <code>X-Limit</code>, <code>X-Total-Pages</code></li>
                    <li><strong>Clientes:</strong> Paginación en body JSON</li>
                    <li>Usa <code>page=N</code> para navegar entre páginas</li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h6><i className="bi bi-images me-1"></i>Media (Fotos y Videos)</h6>
                  <ul className="small text-muted mb-0">
                    <li>Cada producto incluye <code>imagenes[]</code> y <code>videos[]</code></li>
                    <li>Hasta <strong>20 fotos</strong> y <strong>6 videos</strong> por producto</li>
                    <li>Las URLs son absolutas: <code>https://sistema.oftalmetryc.com/cdn/...</code></li>
                    <li>Imágenes optimizadas en JPG (calidad 85, max 2048px)</li>
                    <li>Videos optimizados para WhatsApp (MP4 H.264, max 720p)</li>
                    <li>Campo <code>es_principal: true</code> indica la imagen principal</li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h6><i className="bi bi-shield-check me-1"></i>Errores comunes</h6>
                  <ul className="small text-muted mb-0">
                    <li><code>401</code> — API Key faltante o inválida</li>
                    <li><code>403</code> — Sin permiso o IP no autorizada</li>
                    <li><code>404</code> — Recurso no encontrado</li>
                    <li><code>409</code> — Duplicado (al crear cliente existente)</li>
                    <li><code>429</code> — Rate limit excedido</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB: API Keys Management */}
      {activeTab === 'keys' && (
        <div>
          <div className="d-flex justify-content-between mb-3">
            <h5 className="mb-0">Gestión de API Keys</h5>
            <button className="btn btn-primary" onClick={() => { resetForm(); setShowCreateModal(true); }}>
              <i className="bi bi-plus-lg me-1"></i> Nueva API Key
            </button>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary"></div>
            </div>
          ) : apiKeys.length === 0 ? (
            <div className="card border-0 shadow-sm">
              <div className="card-body text-center py-5">
                <i className="bi bi-key display-1 text-muted"></i>
                <h5 className="mt-3 text-muted">No hay API Keys creadas</h5>
                <p className="text-muted">Crea una API Key para comenzar a usar la API pública</p>
                <button className="btn btn-primary" onClick={() => { resetForm(); setShowCreateModal(true); }}>
                  <i className="bi bi-plus-lg me-1"></i> Crear Primera API Key
                </button>
              </div>
            </div>
          ) : (
            <div className="row g-3">
              {apiKeys.map(key => (
                <div key={key.id} className="col-12">
                  <div className={`card border-0 shadow-sm ${!key.activa ? 'opacity-75' : ''}`}>
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <h6 className="mb-0">{key.nombre}</h6>
                            <span className={`badge ${key.activa ? 'bg-success' : 'bg-danger'}`}>
                              {key.activa ? 'Activa' : 'Inactiva'}
                            </span>
                          </div>
                          {key.descripcion && <p className="text-muted small mb-2">{key.descripcion}</p>}

                          <div className="d-flex align-items-center gap-2 mb-2">
                            <code className="bg-light px-2 py-1 rounded small">{key.api_key}</code>
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => copyToClipboard(key.api_key)} title="Copiar">
                              <i className="bi bi-clipboard"></i>
                            </button>
                          </div>

                          <div className="d-flex flex-wrap gap-1 mb-2">
                            {(key.permisos || []).map((p, i) => (
                              <span key={i} className="badge bg-primary bg-opacity-10 text-primary">{p}</span>
                            ))}
                          </div>

                          <div className="d-flex gap-3 text-muted small">
                            <span><i className="bi bi-speedometer2 me-1"></i>Rate: {key.rate_limit}/h</span>
                            <span><i className="bi bi-graph-up me-1"></i>Requests: {key.total_requests || 0}</span>
                            {key.ultimo_uso && (
                              <span><i className="bi bi-clock me-1"></i>Último uso: {new Date(key.ultimo_uso).toLocaleString()}</span>
                            )}
                            {key.creado_por_nombre && (
                              <span><i className="bi bi-person me-1"></i>{key.creado_por_nombre}</span>
                            )}
                          </div>
                        </div>

                        <div className="d-flex gap-1">
                          <button className="btn btn-sm btn-outline-primary" onClick={() => openEdit(key)} title="Editar">
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button className="btn btn-sm btn-outline-warning" onClick={() => handleRegenerar(key.id, key.nombre)} title="Regenerar Key">
                            <i className="bi bi-arrow-repeat"></i>
                          </button>
                          <button
                            className={`btn btn-sm ${key.activa ? 'btn-outline-secondary' : 'btn-outline-success'}`}
                            onClick={() => handleToggleActive(key)}
                            title={key.activa ? 'Desactivar' : 'Activar'}
                          >
                            <i className={`bi ${key.activa ? 'bi-pause-circle' : 'bi-play-circle'}`}></i>
                          </button>
                          <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(key.id, key.nombre)} title="Eliminar">
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modal Crear/Editar API Key */}
      {(showCreateModal || editingKey) && (
        <>
          <div className="modal-backdrop fade show" onClick={() => { setShowCreateModal(false); setEditingKey(null); }} />
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content border-0 shadow">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="bi bi-key me-2"></i>
                    {editingKey ? 'Editar API Key' : 'Nueva API Key'}
                  </h5>
                  <button type="button" className="btn-close" onClick={() => { setShowCreateModal(false); setEditingKey(null); }} />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Nombre *</label>
                    <input
                      type="text" className="form-control"
                      value={formData.nombre}
                      onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                      placeholder="Ej: Bot WhatsApp, n8n Producción"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Descripción</label>
                    <textarea
                      className="form-control" rows="2"
                      value={formData.descripcion}
                      onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                      placeholder="Descripción del uso de esta API Key"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Permisos</label>
                    <div className="row g-2">
                      {permisosDisponibles.map(p => (
                        <div key={p.key} className="col-md-4">
                          <div
                            className={`border rounded p-2 cursor-pointer ${formData.permisos.includes(p.key) ? 'border-primary bg-primary bg-opacity-10' : ''}`}
                            style={{ cursor: 'pointer' }}
                            onClick={() => togglePermiso(p.key)}
                          >
                            <div className="form-check mb-0">
                              <input
                                type="checkbox" className="form-check-input"
                                checked={formData.permisos.includes(p.key)}
                                onChange={() => togglePermiso(p.key)}
                              />
                              <label className="form-check-label small fw-semibold">{p.label}</label>
                              <div className="text-muted" style={{ fontSize: '0.7rem' }}>{p.desc}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label fw-semibold">Rate Limit (por hora)</label>
                      <input
                        type="number" className="form-control"
                        value={formData.rate_limit}
                        onChange={(e) => setFormData({ ...formData, rate_limit: parseInt(e.target.value) || 100 })}
                        min="10" max="10000"
                      />
                    </div>
                    <div className="col-md-8">
                      <label className="form-label fw-semibold">IP Whitelist <span className="text-muted small">(opcional)</span></label>
                      <input
                        type="text" className="form-control"
                        value={formData.ip_whitelist}
                        onChange={(e) => setFormData({ ...formData, ip_whitelist: e.target.value })}
                        placeholder="IPs separadas por coma: 192.168.1.1, 10.0.0.1"
                      />
                      <div className="form-text">Dejar vacío para permitir cualquier IP</div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-light" onClick={() => { setShowCreateModal(false); setEditingKey(null); }}>
                    Cancelar
                  </button>
                  <button className="btn btn-primary" onClick={editingKey ? handleUpdate : handleCreate}>
                    <i className="bi bi-check-lg me-1"></i>
                    {editingKey ? 'Guardar Cambios' : 'Crear API Key'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ApiDocs;
